// Story 1.19 — TanStack Query hook for GET /api/v1/account/delete-request.
//
// Returns the user's current/most-recent deletion request (or null when
// the user has never requested deletion). Drives the DangerZonePage's
// status-driven rendering: null → IneligibilityChecklist + DeleteModal
// trigger; status='pending' → PendingDeletionPanel; status='completed'
// → CompletedDeletionPanel; status='cancelled' → DeleteModal trigger
// with a small "Previous request cancelled" note.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type DeletionRequestStatus = {
  deletion_request_id: string;
  status: "pending" | "processing" | "completed" | "cancelled";
  scheduled_for: string;
  cancelled_at: string | null;
  completed_at: string | null;
  created_at: string;
  cancellable: boolean;
};

export type DeletionRequestStatusEnvelope = {
  data: DeletionRequestStatus | null;
};

async function fetchDeletionStatus(): Promise<DeletionRequestStatusEnvelope> {
  const response = await fetch("/api/v1/account/delete-request", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`delete-request status fetch failed: ${response.status}`);
  }
  return (await response.json()) as DeletionRequestStatusEnvelope;
}

export function useAccountDeletionStatus() {
  return useQuery({
    queryKey: ["account-deletion-status"],
    queryFn: fetchDeletionStatus,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
